import { nanoid } from "nanoid";
import { watch } from "vue";
import { guideIconHtml } from "@scalar-org/guide-icons";
import { html } from "../../helpers/html-template.js";
import { EditorClasses } from "../../styles.js";
const hydrate = async (options) => {
  if (!options?.isDark) {
    console.warn("hydrateMermaid called without isDark ref");
  }
  const isDark = options?.isDark ?? { value: false };
  const mermaidElements = document.querySelectorAll(
    "pre.mermaid:not([hydrated])"
  );
  if (mermaidElements.length === 0) {
    return true;
  }
  try {
    const mermaid = await import("mermaid").then((mod) => mod.default);
    const diagrams = [];
    const cache = /* @__PURE__ */ new Map();
    const wrapMermaidElement = async (element, id) => {
      const container = document.createElement("div");
      container.className = `${EditorClasses.Mermaid}-container`;
      container.dataset.mermaidId = id;
      const wrapper = document.createElement("div");
      wrapper.className = `${EditorClasses.Mermaid}-wrapper`;
      wrapper.setAttribute("tabindex", "0");
      wrapper.setAttribute("role", "application");
      wrapper.setAttribute(
        "aria-label",
        "Mermaid diagram - Use arrow keys to pan, + and - to zoom"
      );
      const controls = await svgPanZoomControls();
      element.parentNode?.insertBefore(container, element);
      wrapper.appendChild(element);
      container.appendChild(controls);
      container.appendChild(wrapper);
      return { container, controls, wrapper };
    };
    const setSvgHeight = (svgElement) => {
      if (!(svgElement instanceof SVGGraphicsElement)) {
        console.warn("Element is not an SVGGraphicsElement");
        return;
      }
      try {
        const bbox = svgElement.getBBox();
        if (bbox.height > 0) {
          svgElement.style.height = `${bbox.height}px`;
        }
      } catch (error) {
        console.warn("Failed to get SVG bounding box:", error);
      }
    };
    const initializeControls = async (diagram, skipSetHeight = false) => {
      const svgElement = diagram.element.querySelector("svg");
      if (!svgElement) return;
      if (!skipSetHeight) {
        setSvgHeight(svgElement);
      }
      if (diagram.cleanupHandlers) {
        diagram.cleanupHandlers();
        diagram.cleanupHandlers = void 0;
      }
      const panZoomInstance = await initializePanZoom(svgElement);
      if (!panZoomInstance) return;
      diagram.panZoomInstance = panZoomInstance;
      if (diagram.controls && diagram.wrapper) {
        const cleanup = attachControlHandlers({
          controls: diagram.controls,
          getPanZoomInstance: () => diagram.panZoomInstance,
          wrapper: diagram.wrapper
        });
        diagram.cleanupHandlers = cleanup;
      }
    };
    const renderDiagram = async (diagram) => {
      const { element, code, id } = diagram;
      const theme = isDark.value ? "dark" : "default";
      const darkMode = !!isDark.value;
      mermaid.initialize({
        startOnLoad: false,
        theme,
        darkMode
      });
      const originalInner = element.innerHTML;
      try {
        const { svg } = await mermaid.render(id, code);
        element.innerHTML = svg;
        element.style.removeProperty("display");
        const cacheEntry = cache.get(id) || {};
        if (darkMode) {
          cacheEntry.darkMode = svg;
        } else {
          cacheEntry.lightMode = svg;
        }
        cache.set(id, cacheEntry);
        await initializeControls(diagram);
      } catch (error) {
        console.error("Failed to render mermaid diagram:", error);
        element.style.removeProperty("display");
        element.innerHTML = originalInner;
      }
    };
    const renderPromises = Array.from(mermaidElements).map(async (element) => {
      const code = element.textContent?.trim();
      if (!code) return;
      const id = `mermaid-${nanoid()}`;
      const { container, controls, wrapper } = await wrapMermaidElement(
        element,
        id
      );
      element.dataset.mermaidId = id;
      element.setAttribute("hydrated", "true");
      const diagram = { element, code, id, container, controls, wrapper };
      diagrams.push(diagram);
      await renderDiagram(diagram);
    });
    await Promise.all(renderPromises).catch((error) => {
      console.error("Error hydrating mermaid diagrams:", error);
    });
    watch(isDark, async () => {
      const darkMode = isDark.value;
      const updatePromises = diagrams.map(async (diagram) => {
        const { element, id, panZoomInstance } = diagram;
        const cacheEntry = cache.get(id);
        if (!cacheEntry) return;
        const cachedSvg = darkMode ? cacheEntry.darkMode : cacheEntry.lightMode;
        destroyPanZoom(panZoomInstance);
        if (cachedSvg) {
          element.innerHTML = cachedSvg;
          element.style.removeProperty("display");
          const svgElement = element.querySelector("svg");
          if (svgElement) {
            setSvgHeight(svgElement);
          }
          await initializeControls(diagram, true);
        } else {
          await renderDiagram(diagram);
        }
      });
      await Promise.all(updatePromises).catch((error) => {
        console.error("Failed to update mermaid theme:", error);
      });
    });
    return true;
  } catch (error) {
    console.error("Failed to load mermaid library:", error);
    return false;
  }
};
const attachControlHandlers = (options) => {
  const { controls, getPanZoomInstance, wrapper } = options;
  const zoomInButton = controls.querySelector('[data-action="zoom-in"]');
  const zoomOutButton = controls.querySelector('[data-action="zoom-out"]');
  const resetButton = controls.querySelector('[data-action="reset"]');
  const handleZoomIn = () => {
    const panZoomInstance = getPanZoomInstance();
    if (panZoomInstance) {
      panZoomInstance.zoomIn();
    }
  };
  const handleZoomOut = () => {
    const panZoomInstance = getPanZoomInstance();
    if (panZoomInstance) {
      panZoomInstance.zoomOut();
    }
  };
  const handleReset = () => {
    const panZoomInstance = getPanZoomInstance();
    if (panZoomInstance) {
      panZoomInstance.resetZoom();
      panZoomInstance.resetPan();
    }
  };
  const handleKeyDown = (event) => {
    const panZoomInstance = getPanZoomInstance();
    if (!panZoomInstance) return;
    const panStep = 50;
    if (event.key === "ArrowUp") {
      event.preventDefault();
      panZoomInstance.panBy({ x: 0, y: panStep });
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      panZoomInstance.panBy({ x: 0, y: -panStep });
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      panZoomInstance.panBy({ x: panStep, y: 0 });
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      panZoomInstance.panBy({ x: -panStep, y: 0 });
    } else if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      handleZoomIn();
    } else if (event.key === "-" || event.key === "_") {
      event.preventDefault();
      handleZoomOut();
    } else if (event.key === "0" || event.key === "r" || event.key === "R") {
      event.preventDefault();
      handleReset();
    }
  };
  zoomInButton?.addEventListener("click", handleZoomIn);
  zoomOutButton?.addEventListener("click", handleZoomOut);
  resetButton?.addEventListener("click", handleReset);
  wrapper.addEventListener("keydown", handleKeyDown);
  return () => {
    zoomInButton?.removeEventListener("click", handleZoomIn);
    zoomOutButton?.removeEventListener("click", handleZoomOut);
    resetButton?.removeEventListener("click", handleReset);
    wrapper.removeEventListener("keydown", handleKeyDown);
  };
};
const initializePanZoom = async (svgElement) => {
  const svgPanZoom = await import("svg-pan-zoom").then((mod) => mod.default);
  const bbox = svgElement.getBoundingClientRect();
  if (bbox.width === 0 || bbox.height === 0) {
    console.warn("SVG has no dimensions, cannot initialize pan-zoom");
    return null;
  }
  try {
    return svgPanZoom(svgElement, {
      zoomEnabled: true,
      controlIconsEnabled: false,
      fit: true,
      center: true,
      minZoom: 0.1,
      maxZoom: 10,
      zoomScaleSensitivity: 0.3,
      mouseWheelZoomEnabled: true,
      dblClickZoomEnabled: true,
      preventMouseEventsDefault: true
    });
  } catch (error) {
    console.error("Failed to initialize svg-pan-zoom:", error);
    return null;
  }
};
const destroyPanZoom = (instance) => {
  instance?.destroy();
};
async function svgPanZoomControls() {
  const controls = document.createElement("div");
  controls.className = `${EditorClasses.Mermaid}-controls`;
  controls.innerHTML = html`
    <button class="${EditorClasses.Mermaid}-control-btn zoom-in" aria-label="Zoom in" data-action="zoom-in" title="Zoom in">
      ${await guideIconHtml({ src: "magnifying-glass-plus" })}
    </button>
    <button class="${EditorClasses.Mermaid}-control-btn zoom-out" aria-label="Zoom out" data-action="zoom-out" title="Zoom out">
      ${await guideIconHtml({ src: "magnifying-glass-minus" })}
    </button>
    <button class="${EditorClasses.Mermaid}-control-btn reset" aria-label="Reset view" data-action="reset" title="Reset view">
      ${await guideIconHtml({ src: "arrow-counter-clockwise" })}
    </button>`;
  return controls;
}
export {
  hydrate
};
//# sourceMappingURL=hydration.js.map
